import React, { useMemo, useCallback, useState, useRef } from 'react';
import { 
  useGetConditionResourcesLookupsQuery,
  useConditionResourceFilters,
  useConditionResourceActions,
  ConditionResourceDetailsModal,
  createResourcesColumns,
  createResourcesActions,
  RESOURCE_TYPE_OPTIONS,
  useResourceBulkChangeStatusMutation,
  ConditionResourceLookup,
  CONDITION_RESOURCES_FEATURES,
} from '@entities/condition-resources';
import { ConditionResourceCreateModal, ConditionResourceEditModal } from '@entities/condition-resources/components/ConditionResourceCreateEditModal';
import { Box, VStack } from '@chakra-ui/react';
import { FileText, RefreshCw } from 'lucide-react';
import PageHeader from '@/components/ui/PageHeader';
import SearchAndFiltersSection, { Filter } from '@/components/section/SearchAndFiltersSection';
import EntityTableSection from '@/components/section/EntityTableSection';
import type { BulkAction } from '@/components/section/EntityTableSection';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import ErrorAlert from '@/components/ErrorAlert';
import Pagination from '@/components/ui/Pagination';
import { LOOKUP_STATUS_FILTER_OPTIONS, LOOKUP_STATUS_OPTIONS, getStatusBadgeProps } from '@/utils/lookupStatus';
import type { LookupStatus } from '@/utils/lookupStatus';
import { useMultiSelect } from '@/hooks/useMultiSelect';
import { useModal } from '@/hooks/useModal';

const SeedResourcesPage: React.FC = () => {
  const { filters, queryParams, handleFilterChange } = useConditionResourceFilters();
  const actions = useConditionResourceActions();
  
  const {
    data: paginatedResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetConditionResourcesLookupsQuery({
    ...queryParams,
    type: 'resource',
  }, {
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const resourcesArray = paginatedResponse?.data || [];
  const filteredResources = resourcesArray;

  // Multi-select
  const multiSelect = useMultiSelect(resourcesArray);
  const [bulkChangeStatus, { isLoading: isBulkUpdating }] = useResourceBulkChangeStatusMutation();
  const bulkConfirmModal = useModal();
  const [pendingBulkStatus, setPendingBulkStatus] = useState<LookupStatus | null>(null);

  const requestBulkStatusChange = useCallback((status: LookupStatus) => {
    setPendingBulkStatus(status);
    bulkConfirmModal.open();
  }, [bulkConfirmModal]);

  const handleBulkStatusConfirm = useCallback(async () => {
    if (!pendingBulkStatus) return;
    const ids = Array.from(multiSelect.selectedIds) as number[];
    try {
      await bulkChangeStatus({ ids, status: pendingBulkStatus }).unwrap();
      multiSelect.clearSelection();
      bulkConfirmModal.close();
      setPendingBulkStatus(null);
    } catch (err) {
      console.error('Bulk status update failed:', err);
    }
  }, [pendingBulkStatus, multiSelect.selectedIds, bulkChangeStatus, multiSelect.clearSelection, bulkConfirmModal]);

  const handleBulkConfirmClose = useCallback(() => {
    bulkConfirmModal.close();
    setPendingBulkStatus(null);
  }, [bulkConfirmModal]);

  const bulkActions: BulkAction[] = useMemo(() => {
    if (multiSelect.selectedCount === 0) return [];
    return [
      {
        key: 'changeStatus',
        label: 'Change Status',
        icon: RefreshCw,
        items: LOOKUP_STATUS_OPTIONS.map((opt) => ({
          key: opt.value,
          label: opt.label,
          onClick: () => requestBulkStatusChange(opt.value as LookupStatus),
        })),
      },
    ];
  }, [multiSelect.selectedCount, requestBulkStatusChange]);

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => 
    createResourcesColumns(), []
  );

  // Memoize table actions
  const tableActions = useMemo(() => 
    createResourcesActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler
  const handleSearchChange = useCallback(
    (value: string) => {
      handleFilterChange("search", value);
    },
    [handleFilterChange]
  );

  // Memoize filter change handlers to prevent recreating on every render
  const handleResourceTypeFilterChange = useCallback(
    (value: string) => handleFilterChange("resourceType", value),
    [handleFilterChange]
  );
  
  const handleStatusFilterChange = useCallback(
    (value: string) => handleFilterChange("status", value),
    [handleFilterChange]
  );

  const handleValidationStatusFilterChange = useCallback(
    (value: string) => handleFilterChange("validationStatus", value),
    [handleFilterChange]
  );

  // Memoize options arrays to prevent recreating on every render
  const resourceTypeOptions = useMemo(
    () => [
      { value: "all", label: "All Resource Types" },
      ...RESOURCE_TYPE_OPTIONS,
    ],
    []
  );

  const validationStatusOptions = useMemo(
    () => [
      { value: "all", label: "All Validation" },
      { value: "valid", label: "Valid" },
      { value: "invalid", label: "Invalid" },
      { value: "unknown", label: "Unknown" },
    ],
    []
  );

  // Memoize search filters configuration
  const searchFilters: Filter[] = useMemo(
    () => [
      {
        key: "resourceType",
        label: "All Resource Types",
        value: filters.resourceType,
        options: resourceTypeOptions,
        onChange: handleResourceTypeFilterChange,
      },
      ...(CONDITION_RESOURCES_FEATURES.SHOW_STATUS_FILTER ? [{
        key: "status" as const,
        label: "All Status",
        value: filters.status,
        options: LOOKUP_STATUS_FILTER_OPTIONS,
        onChange: handleStatusFilterChange,
      }] : []),
      {
        key: "validationStatus",
        label: "All Validation",
        value: filters.validationStatus,
        options: validationStatusOptions,
        onChange: handleValidationStatusFilterChange,
      },
    ],
    [filters.resourceType, filters.status, filters.validationStatus, handleResourceTypeFilterChange, handleStatusFilterChange, handleValidationStatusFilterChange, resourceTypeOptions, validationStatusOptions]
  );

  // Memoize modal success handler
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Memoize page change handler
  const handlePageChange = useCallback((page: number) => {
    handleFilterChange('page', page);
  }, [handleFilterChange]);

  // Memoize page size change handler
  const handlePageSizeChange = useCallback((pageSize: number) => {
    handleFilterChange('limit', pageSize);
  }, [handleFilterChange]);

  if (isLoading && !resourcesArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load resources"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto">
      <VStack gap={6} align="stretch">
        <PageHeader
          title="Seed Resources"
          subtitle={`${filteredResources.length} resources (${paginatedResponse?.total || 0} total)`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Add Resource"
          bulkActions={CONDITION_RESOURCES_FEATURES.SHOW_BULK_SELECT ? bulkActions : undefined}
          selectedCount={multiSelect.selectedCount}
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search resources by title or description..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection<ConditionResourceLookup>
          data={filteredResources}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: FileText,
            title: "No resource content found",
            description: "Create your first resource to get started",
          }}
          multiSelect={CONDITION_RESOURCES_FEATURES.SHOW_BULK_SELECT ? {
            selectedIds: multiSelect.selectedIds,
            onToggle: multiSelect.toggle,
            onToggleAll: multiSelect.toggleAll,
            isAllSelected: multiSelect.isAllSelected,
            getItemId: (item) => item.id,
          } : undefined}
        />

        {paginatedResponse && (
          <Pagination
            paginationInfo={{
              current_page: paginatedResponse.current_page,
              last_page: paginatedResponse.last_page,
              per_page: paginatedResponse.per_page,
              total: paginatedResponse.total,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            isLoading={isFetching}
          />
        )}

        <ConditionResourceDetailsModal />

        <ConditionResourceCreateModal
          resourceType="resource"
          onSuccess={handleModalSuccess}
        />

        <ConditionResourceEditModal
          resourceType="resource"
          onSuccess={handleModalSuccess}
        />

        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}

        {bulkConfirmModal.isOpen && pendingBulkStatus && (
          <ConfirmationModal
            isOpen={bulkConfirmModal.isOpen}
            onClose={handleBulkConfirmClose}
            onConfirm={handleBulkStatusConfirm}
            title="Confirm Bulk Status Change"
            message={`Are you sure you want to change ${multiSelect.selectedCount} resource${multiSelect.selectedCount > 1 ? 's' : ''} to "${getStatusBadgeProps(pendingBulkStatus).label}"?`}
            confirmText="Change Status"
            isLoading={isBulkUpdating}
            type="warning"
          />
        )}
      </VStack>
    </Box>
  );
};

export default SeedResourcesPage;
